import { useCallback, useEffect, useRef } from "react";
import posthog from "posthog-js";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import { setModalOpenState } from "../actions/ui";
import { isAdminDomain } from "../actions/auth";
import { getAdsIntegrationKey } from "../actions/getters";
import {
  setAdleCardWaitlistStatus,
  startListenerAdleCardWaitlistStatus,
} from "../actions/adleCardWaitlist";
import { isPrivilegedLoginFromStorage } from "../utils/privilegedLoginStorage";
import { getMaxModalLayerZIndex } from "../utils/modalLayer";
import {
  ADVERTISER_ROLE,
  GOOGLE,
  IS_ADLE_DOMAIN,
  IS_WHITE_LABEL_BRAND,
  LINKEDIN,
  META,
  TIKTOK,
} from "../utils/constants";
import {
  ADLE_CARD_WAITLIST_SOURCE,
  getIsAdleCardWaitlistEligible,
  getShouldAutoOpenAdleCardWaitlist,
} from "../utils/adleCardWaitlist";

const AUTO_OPEN_DELAY_MS = 3000;
const AUTO_OPEN_RETRY_MS = 500;
const MODAL_NAME = "isModalAdleCardWaitlist";
const AD_PLATFORMS = [META, GOOGLE, TIKTOK, LINKEDIN];
const UNRESOLVED_STATUS = {
  isStatusResolved: false,
  hasJoined: false,
  hasDismissed: false,
};

const buildAdleCardWaitlistEligibilityInput = ({
  user,
  payments,
  integrations,
}) => ({
  user,
  payments,
  integrations,
  adAccountIntegrationKeys: AD_PLATFORMS.map((platform) =>
    getAdsIntegrationKey(platform)
  ),
  isPrivilegedLogin: isPrivilegedLoginFromStorage({ uid: user?.uid }),
  isAdleDomain: IS_ADLE_DOMAIN,
  isWhiteLabelBrand: Boolean(IS_WHITE_LABEL_BRAND),
  isAdminHost: isAdminDomain(),
});

export const useIsAdleCardWaitlistEligible = () => {
  const user = useSelectorApp((state) => state.auth.user);
  const payments = useSelectorApp((state) => state.payments);
  const integrations = useSelectorApp((state) => state.user.integrations);

  return getIsAdleCardWaitlistEligible(
    buildAdleCardWaitlistEligibilityInput({ user, payments, integrations })
  );
};

const useAdleCardWaitlist = ({ showWelcomeModal = false } = {}) => {
  const dispatch = useDispatchApp();
  const user = useSelectorApp((state) => state.auth.user);
  const uid = user?.uid;
  const openModals = useSelectorApp((state) => state.ui.openModals);
  const waitlist = useSelectorApp((state) => state.ui.adleCardWaitlist);
  const isEligible = useIsAdleCardWaitlistEligible();
  const isModalOpen = openModals?.isModalAdleCardWaitlist === true;
  const viewedRef = useRef(false);
  const autoOpenConsumedRef = useRef(false);
  const autoOpenUidRef = useRef(uid);
  const autoOpenDelayTimerRef = useRef(null);
  const autoOpenRetryTimerRef = useRef(null);

  const hasBlockingModal = Object.entries(openModals || {}).some(
    ([name, isOpen]) => name !== MODAL_NAME && isOpen === true
  );

  const blockersRef = useRef({});
  blockersRef.current = {
    showWelcomeModal,
    isModalOpen,
    hasBlockingModal,
  };

  useEffect(() => {
    if (uid !== autoOpenUidRef.current) {
      autoOpenUidRef.current = uid;
      autoOpenConsumedRef.current = false;
    }
  }, [uid]);

  const clearAutoOpenTimers = useCallback(() => {
    if (autoOpenDelayTimerRef.current != null) {
      clearTimeout(autoOpenDelayTimerRef.current);
      autoOpenDelayTimerRef.current = null;
    }
    if (autoOpenRetryTimerRef.current != null) {
      clearTimeout(autoOpenRetryTimerRef.current);
      autoOpenRetryTimerRef.current = null;
    }
  }, []);

  // RTDB status is independent of eligibility (integrations/plan can resolve later).
  useEffect(() => {
    if (!uid || user?.isAnonymous || user?.role !== ADVERTISER_ROLE) {
      return undefined;
    }

    const unsubscribe = dispatch(
      startListenerAdleCardWaitlistStatus({
        uid,
        onUpdatedDataCallback: (next) => {
          dispatch(setAdleCardWaitlistStatus(next));
        },
      })
    );

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
      dispatch(setAdleCardWaitlistStatus(UNRESOLVED_STATUS));
    };
  }, [dispatch, uid, user?.isAnonymous, user?.role]);

  const tryAutoOpen = useCallback(() => {
    if (autoOpenConsumedRef.current) return;

    const {
      showWelcomeModal: welcome,
      isModalOpen: waitlistOpen,
      hasBlockingModal: blocked,
    } = blockersRef.current;

    const maxLayerZ = getMaxModalLayerZIndex();
    if (welcome || waitlistOpen || blocked || maxLayerZ > 0) {
      autoOpenRetryTimerRef.current = setTimeout(
        tryAutoOpen,
        AUTO_OPEN_RETRY_MS
      );
      return;
    }

    autoOpenConsumedRef.current = true;
    dispatch(
      setAdleCardWaitlistStatus({
        openSource: ADLE_CARD_WAITLIST_SOURCE.AUTO,
      })
    );
    dispatch(setModalOpenState(MODAL_NAME, true));
  }, [dispatch]);

  useEffect(() => {
    const shouldSchedule = getShouldAutoOpenAdleCardWaitlist({
      isEligible,
      isStatusResolved: waitlist?.isStatusResolved === true,
      hasJoined: waitlist?.hasJoined === true,
      hasDismissed: waitlist?.hasDismissed === true,
      hasAutoOpenedThisPageLoad: autoOpenConsumedRef.current,
    });

    clearAutoOpenTimers();

    if (!shouldSchedule || !uid) return undefined;

    autoOpenDelayTimerRef.current = setTimeout(() => {
      autoOpenDelayTimerRef.current = null;
      tryAutoOpen();
    }, AUTO_OPEN_DELAY_MS);

    return clearAutoOpenTimers;
  }, [
    clearAutoOpenTimers,
    isEligible,
    tryAutoOpen,
    uid,
    waitlist?.hasDismissed,
    waitlist?.hasJoined,
    waitlist?.isStatusResolved,
  ]);

  useEffect(() => {
    if (!isModalOpen) {
      viewedRef.current = false;
      return;
    }
    if (viewedRef.current) return;
    viewedRef.current = true;
    posthog.capture("adle_card_waitlist_viewed", {
      source: waitlist?.openSource || ADLE_CARD_WAITLIST_SOURCE.AUTO,
    });
  }, [isModalOpen, waitlist?.openSource]);
};

export default useAdleCardWaitlist;
